import { CVSS31 } from "../../../utils/cvss31/cvsscalc31";
import { getVulnerabilityColour } from "../../reports/VulnerabilityFlag";
import { ButtonOptions, ButtonOption } from "./ControlledButtonOptions";
import { useEffect, useState } from "react";
import { CVSS40Calculator, CVSS40CalculationResult } from "./CVSS40Calculator";
import { CVSS40 } from "@pandatix/js-cvss";

export type SeverityScore = {
  category: string;
  score: string;
  values: SeverityValues;
  cvss_version?: "3.1" | "4.0";
  cvss_vector?: string;
  cvss_score?: number;
};

type CVSSRange = "N" | "L" | "H";

type SeverityValues = {
  AV: "N" | "A" | "L" | "P" | undefined;
  AC: "L" | "H" | undefined;
  PR: CVSSRange | undefined;
  UI: "N" | "R" | undefined;
  S: "U" | "C" | undefined;
  C: CVSSRange | undefined;
  I: CVSSRange | undefined;
  A: CVSSRange | undefined;
};

export const blankSeverityScore: SeverityScore = {
  category: "None",
  score: "-.-",
  cvss_version: "4.0",
  values: {
    AV: undefined,
    AC: undefined,
    PR: undefined,
    UI: undefined,
    S: undefined,
    C: undefined,
    I: undefined,
    A: undefined
  }
};

/**
 * Convert a CVSS vector string into a type-friendly SeverityScore
 * object (or undefined if the string was invalid.)
 */
export const parseCVSSVector = (vector: string): SeverityScore | undefined => {
  try {
    if (vector && vector.startsWith("CVSS:4.0")) {
      try {
        const cvss = new CVSS40(vector);
        const computedScore = cvss.Score();
        const rating = CVSS40.Rating(computedScore);
        const category = rating.charAt(0) + rating.slice(1).toLowerCase();

        return {
          category,
          score: computedScore.toFixed(1),
          cvss_version: "4.0",
          cvss_vector: vector,
          cvss_score: computedScore,
          values: {
            AV: undefined,
            AC: undefined,
            PR: undefined,
            UI: undefined,
            S: undefined,
            C: undefined,
            I: undefined,
            A: undefined
          }
        };
      } catch {
        return {
          category: "None",
          score: "-.-",
          cvss_version: "4.0",
          cvss_vector: vector,
          cvss_score: undefined,
          values: {
            AV: undefined,
            AC: undefined,
            PR: undefined,
            UI: undefined,
            S: undefined,
            C: undefined,
            I: undefined,
            A: undefined
          }
        };
      }
    }

    const values = {
      AV: undefined,
      AC: undefined,
      PR: undefined,
      UI: undefined,
      S: undefined,
      C: undefined,
      I: undefined,
      A: undefined
    };

    if (vector && vector.startsWith("CVSS:")) {
      const parts = vector.split("/");
      for (const part of parts) {
        const keyAndVal = part.split(":");
        if (keyAndVal.length === 2) {
          const key = keyAndVal[0];
          const val = keyAndVal[1];
          if (key && val && val !== "undefined") {
            if (key in values) {
              (values as any)[key] = val;
            }
          }
        }
      }
    }

    let category = "None";
    let score = "-.-";
    try {
      const calulation = CVSS31.calculateCVSSFromVector(vector);
      if (calulation && (calulation as any).success !== false && (calulation as any).baseSeverity) {
        category = (calulation as any).baseSeverity;
        score = (calulation as any).baseMetricScore;
      }
    } catch {
      // Allow fallback to None / -.- for incomplete vectors
    }

    return {
      category,
      score,
      cvss_version: "3.1",
      cvss_vector: vector,
      cvss_score: score !== "-.-" ? parseFloat(score) : undefined,
      values
    };
  } catch {
    return undefined;
  }
};

const CategorySelector = <T extends string | undefined>({
  label,
  options,
  value,
  onChange,
  disabled
}: {
  label: string;
  options: ButtonOption<T>[];
  value: T;
  onChange: (value: T) => void;
  disabled?: boolean;
}) => (
  <div className="mb-5">
    <h2 className="mb-1 text-xs font-semibold text-slate-800">{label}</h2>
    <ButtonOptions
      value={value}
      options={options.map(opt => ({ ...opt, isDisabled: disabled || opt.isDisabled }))}
      onChange={value => value !== undefined && onChange(value)}
      className="bg-white px-2.5 py-1 text-xs"
    />
  </div>
);

const SeverityScoreSelector = ({
  value,
  onChange,
  disabled
}: {
  value: SeverityScore;
  onChange: (value: SeverityScore) => void;
  disabled?: boolean;
}) => {
  const initialVersion = value?.cvss_version || (value?.cvss_vector?.startsWith("CVSS:3.1") ? "3.1" : "4.0");
  const [version, setVersion] = useState<"3.1" | "4.0">(initialVersion);
  const [state, setState] = useState(value ?? { ...blankSeverityScore, cvss_version: "4.0" });
  const [saved40Vector, setSaved40Vector] = useState<string | undefined>(
    value?.cvss_vector?.startsWith("CVSS:4.0") ? value.cvss_vector : undefined
  );

  const updateState = (callback: (state: SeverityScore) => SeverityScore) => {
    let newState = callback(state);
    // Update the displayed score
    newState = calculateScore(newState);
    newState.cvss_version = "3.1";

    setState(newState);
    onChange(newState);
  };

  const calculateScore = (severity: SeverityScore) => {
    const calculation = CVSS31.calculateCVSSFromMetrics.apply(
      null,
      Object.values(severity.values) as any
    );

    const v = severity.values;
    const parts: string[] = [];
    if (v?.AV) parts.push(`AV:${v.AV}`);
    if (v?.AC) parts.push(`AC:${v.AC}`);
    if (v?.PR) parts.push(`PR:${v.PR}`);
    if (v?.UI) parts.push(`UI:${v.UI}`);
    if (v?.S) parts.push(`S:${v.S}`);
    if (v?.C) parts.push(`C:${v.C}`);
    if (v?.I) parts.push(`I:${v.I}`);
    if (v?.A) parts.push(`A:${v.A}`);

    const cvssVector = parts.length > 0 ? `CVSS:3.1/${parts.join("/")}` : severity.cvss_vector;
    const numScore = calculation.baseMetricScore && calculation.baseMetricScore !== "-.-" ? parseFloat(calculation.baseMetricScore) : undefined;

    return {
      ...severity,
      score: calculation.baseMetricScore ?? "-.-",
      category: calculation.baseSeverity
        ? `${calculation.baseSeverity}`
        : "None",
      cvss_version: "3.1" as const,
      cvss_vector: cvssVector,
      cvss_score: numScore
    };
  };

  // Update the severity score state when the value changes outside of this component
  useEffect(() => {
    if (value !== undefined) {
      setState(value);
      const targetVersion = value.cvss_version || (value.cvss_vector?.startsWith("CVSS:3.1") ? "3.1" : "4.0");
      setVersion(targetVersion);
      if (value.cvss_vector?.startsWith("CVSS:4.0")) {
        setSaved40Vector(value.cvss_vector);
      }
    }
  }, [value]);

  const handleVersionChange = (newVersion: "3.1" | "4.0") => {
    setVersion(newVersion);
    if (newVersion === "3.1") {
      const updated: SeverityScore = calculateScore({
        ...state,
        cvss_version: "3.1",
        cvss_vector: undefined,
        cvss_score: undefined
      });
      setState(updated);
      onChange(updated);
    } else {
      const targetVec = saved40Vector || (state.cvss_vector?.startsWith("CVSS:4.0") ? state.cvss_vector : undefined);
      const parsed40 = targetVec ? parseCVSSVector(targetVec) : undefined;

      const updated: SeverityScore = {
        ...state,
        cvss_version: "4.0",
        cvss_vector: targetVec,
        cvss_score: parsed40?.cvss_score ?? state.cvss_score,
        score: parsed40?.score ?? (state.score !== "-.-" ? state.score : "-.-"),
        category: parsed40?.category ?? (state.category !== "Draft" ? state.category : "None")
      };
      setState(updated);
      onChange(updated);
    }
  };

  const handleCvss40Change = (res: CVSS40CalculationResult) => {
    setSaved40Vector(res.vectorString);
    const updated: SeverityScore = {
      category: res.severity,
      score: res.score,
      cvss_version: "4.0",
      cvss_vector: res.vectorString,
      cvss_score: parseFloat(res.score),
      values: state.values
    };
    setState(updated);
    onChange(updated);
  };

  return (
    <div className="relative group">
      {disabled && (
        <>
          <div className="absolute inset-0 z-10 bg-transparent cursor-not-allowed" />
          <div className="absolute bottom-full left-1/2 z-20 mb-2 w-max max-w-xs -translate-x-1/2 rounded bg-slate-900 px-3 py-1.5 text-center text-xs font-medium text-white opacity-0 transition-opacity duration-200 group-hover:opacity-100 pointer-events-none shadow-md">
            Please contact Admin or Sub Admin to change severity
            <div className="absolute top-full left-1/2 h-0 w-0 -translate-x-1/2 border-x-4 border-t-4 border-x-transparent border-t-slate-900" />
          </div>
        </>
      )}

      {/* Version Selector Header */}
      <div className="mb-3 flex items-center justify-between rounded-t-lg bg-slate-100 px-4 py-2 border border-slate-200">
        <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">
          CVSS Standard Version:
        </span>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => handleVersionChange("3.1")}
            className={`px-3 py-1 text-xs font-semibold rounded transition-colors ${
              version === "3.1"
                ? "bg-ctb-blue-500 text-white shadow-sm"
                : "bg-white text-slate-600 hover:bg-slate-200"
            }`}
          >
            CVSS 3.1
          </button>
          <button
            type="button"
            onClick={() => handleVersionChange("4.0")}
            className={`px-3 py-1 text-xs font-semibold rounded transition-colors ${
              version === "4.0"
                ? "bg-ctb-blue-500 text-white shadow-sm"
                : "bg-white text-slate-600 hover:bg-slate-200"
            }`}
          >
            CVSS 4.0
          </button>
        </div>
      </div>

      {version === "4.0" ? (
        <CVSS40Calculator
          initialVector={saved40Vector || state.cvss_vector || value?.cvss_vector}
          onChange={handleCvss40Change}
          disabled={disabled}
        />
      ) : (
      <div className={`rounded-lg bg-ctb-blue-150 p-4 transition-opacity duration-200 ${disabled ? "opacity-60 select-none" : ""}`}>
        {/* Info Header */}
        <article className="mb-3 flex items-center justify-between">
          <div className="mr-4 flex flex-col leading-tight">
            <h4 className="mb-1 text-lg font-semibold text-slate-900">
              Severity Level Calculator{" "}
              <span className="text-ctb-red-500">*</span>
            </h4>
            <p className="flex-grow text-xs font-light text-slate-600">
              We use the Common Vulnerability Scoring System (CVSS 3.1) to assess the
              severity of your vulnerability. Please estimate the severity of this
              vulnerability by selecting a category or using a calculator.
            </p>
          </div>

          <div
            className={
              "flex max-h-[96px] min-h-[96px] min-w-[120px] max-w-[120px] flex-col items-center justify-center gap-0.5 rounded border border-ctb-blue-400 bg-white p-2 shadow-sm"
            }
          >
            <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Score</span>
            <p
              className={`text-${
                value ? getVulnerabilityColour(value) : "black"
              } text-2xl font-bold bg-transparent`}
            >
              {state.score || "-.-"}
            </p>
            <h5
              className={`text-${
                value ? getVulnerabilityColour(value) : "black"
              } text-xs font-medium bg-transparent`}
            >
              ({state.category || "None"})
            </h5>
          </div>
        </article>

        {/* Vector Display Box */}
        <div className="mb-3 rounded border border-slate-200 bg-slate-50 py-1.5 px-2.5 font-mono text-[11px] text-slate-700">
          <span className="font-semibold text-slate-500 mr-2">Vector:</span>
          {(() => {
            const v = state.values;
            const parts: string[] = [];
            if (v?.AV) parts.push(`AV:${v.AV}`);
            if (v?.AC) parts.push(`AC:${v.AC}`);
            if (v?.PR) parts.push(`PR:${v.PR}`);
            if (v?.UI) parts.push(`UI:${v.UI}`);
            if (v?.S) parts.push(`S:${v.S}`);
            if (v?.C) parts.push(`C:${v.C}`);
            if (v?.I) parts.push(`I:${v.I}`);
            if (v?.A) parts.push(`A:${v.A}`);

            if (parts.length > 0) {
              return `CVSS:3.1/${parts.join("/")}`;
            }
            return state.cvss_vector || value?.cvss_vector || "CVSS:3.1";
          })()}
        </div>

        {/* Score selector */}
        <article className="grid w-full grid-cols-[max-content_max-content] gap-7">
          <div className="flex flex-col gap-4">
            <CategorySelector
              label="Attack Vector"
              value={state.values.AV}
              disabled={disabled}
              options={[
                { label: "Network", value: "N" },
                { label: "Adjacent", value: "A" },
                { label: "Local", value: "L" },
                { label: "Physical", value: "P" }
              ]}
              onChange={value =>
                updateState(state => ({
                  ...state,
                  values: {
                    ...state.values,
                    AV: value
                  }
                }))
              }
            />

            <CategorySelector
              label="Attack Complexity"
              value={state.values.AC}
              disabled={disabled}
              options={[
                { label: "Low", value: "L" },
                { label: "High", value: "H" }
              ]}
              onChange={value =>
                updateState(state => ({
                  ...state,
                  values: {
                    ...state.values,
                    AC: value
                  }
                }))
              }
            />

            <CategorySelector
              label="Privilege Required"
              value={state.values.PR}
              disabled={disabled}
              options={[
                { label: "None", value: "N" },
                { label: "Low", value: "L" },
                { label: "High", value: "H" }
              ]}
              onChange={value =>
                updateState(state => ({
                  ...state,
                  values: {
                    ...state.values,
                    PR: value
                  }
                }))
              }
            />

            <CategorySelector
              label="User Interaction"
              value={state.values.UI}
              disabled={disabled}
              options={[
                { label: "None", value: "N" },
                { label: "Required", value: "R" }
              ]}
              onChange={value =>
                updateState(state => ({
                  ...state,
                  values: {
                    ...state.values,
                    UI: value
                  }
                }))
              }
            />
          </div>

          <div className="flex flex-col gap-4">
            <CategorySelector
              label="Scope"
              value={state.values.S}
              disabled={disabled}
              options={[
                { label: "Unchanged", value: "U" },
                { label: "Changed", value: "C" }
              ]}
              onChange={value =>
                updateState(state => ({
                  ...state,
                  values: {
                    ...state.values,
                    S: value
                  }
                }))
              }
            />

            <CategorySelector
              label="Confidentiality"
              value={state.values.C}
              disabled={disabled}
              options={[
                { label: "None", value: "N" },
                { label: "Low", value: "L" },
                { label: "High", value: "H" }
              ]}
              onChange={value =>
                updateState(state => ({
                  ...state,
                  values: {
                    ...state.values,
                    C: value
                  }
                }))
              }
            />

            <CategorySelector
              label="Integrity"
              value={state.values.I}
              disabled={disabled}
              options={[
                { label: "None", value: "N" },
                { label: "Low", value: "L" },
                { label: "High", value: "H" }
              ]}
              onChange={value =>
                updateState(state => ({
                  ...state,
                  values: {
                    ...state.values,
                    I: value
                  }
                }))
              }
            />

            <CategorySelector
              label="Availability"
              value={state.values.A}
              disabled={disabled}
              options={[
                { label: "None", value: "N" },
                { label: "Low", value: "L" },
                { label: "High", value: "H" }
              ]}
              onChange={value =>
                updateState(state => ({
                  ...state,
                  values: {
                    ...state.values,
                    A: value
                  }
                }))
              }
            />
          </div>
        </article>
      </div>
      )}
    </div>
  );
};

export default SeverityScoreSelector;
